import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Clock, Loader2, Pause } from "lucide-react";

import { useAntiCheat } from "@/anticheat/react/useAntiCheat";
import { useContentProtection } from "@/anticheat/react/useContentProtection";
import { useFullscreen } from "@/anticheat/react/useFullscreen";
import { Button } from "@/components/ui/button";
import { CODE_MAX, CodeQuestion, type CodeRunResult } from "@/components/CodeQuestion";
import { AnswerSaveFailureNote } from "@/components/candidate/AnswerSaveFailureNote";
import { CameraPreview } from "@/components/candidate/CameraPreview";
import { CAMERA_TILE_RESERVE, snapshotCaption } from "@/components/candidate/cameraTile";
import { FileUploadAnswer } from "@/components/candidate/FileUploadAnswer";
import { Likert } from "@/components/Likert";
import { FullscreenBlockingOverlay } from "@/components/candidate/FullscreenBlockingOverlay";
import { PasteDetectedModal } from "@/components/candidate/PasteDetectedModal";
import {
  ADVANCE_FAILED_MESSAGE,
  SUBMIT_FAILED_MESSAGE,
  TIME_UP_CLOSE_FAILED_MESSAGE,
  SubmitConfirmDialog,
} from "@/components/candidate/SubmitConfirmDialog";
import { MCQ } from "@/components/MCQ";
import { MCQMulti } from "@/components/MCQMulti";
import { OpenEnded } from "@/components/OpenEnded";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { WarningNote } from "@/components/ui/warning-note";
import {
  classifyAdvanceError,
  classifyQuestionError,
  pausedAttemptOf,
  pausedSectionOf,
  type PausedAttempt,
} from "@/lib/candidateError";
import { hasAnswerFor } from "@/lib/candidateAnswered";
import { captureSilentFailure } from "@/lib/observability";
import {
  ApiError,
  advanceSection,
  bounceToGate,
  emitEvent,
  fetchPreview,
  fetchQuestion,
  fetchReview,
  finishSection,
  loadAttemptId,
  loadCandidateMeta,
  logBrowserRun,
  pauseAttempt,
  saveCandidateMeta,
  submitAnswer,
  submitAttempt,
  type QuestionResponse,
  type SavedFileInfo,
} from "@/lib/api";
import { resolveAnticheat } from "@/lib/anticheatConfig";
import {
  answerPrint,
  clearAnswerDraft,
  createAnswerDraftWriter,
  draftKindOf,
  loadAnswerDraft,
  noteServerAnswer,
  saveAnswerDraft,
  serverAnswerPrint,
  type AnswerDraft,
} from "@/lib/answerDraft";
import {
  answerSaveFailures,
  enqueueAnswerSave,
  flushAnswerSaves,
} from "@/lib/answerQueue";
import { useAnswerSaveFailures } from "@/lib/useAnswerSaveFailures";
import {
  createReplayRecorder,
  flushReplayUploads,
  type ReplayRecorder,
} from "@/lib/codeReplayRecorder";
import {
  attachOwnResults,
  ownTestsPayload,
  runOwnTestsInBrowser,
  useOwnTests,
  type OwnTestResult,
} from "@/lib/ownTests";
import { queryKeys } from "@/lib/queryKeys";
import {
  detectFunctionName,
  getRuntime,
  isServerLanguage,
  languageOf,
  RUN_LOCATION,
  runUserCodeAgainstTest,
} from "@/lib/runtimes";
import { questionContract } from "@/lib/runtimes/registry";
import { runOnServerAsCandidate, type RunFailure } from "@/lib/runtimes/serverRun";
import { useCountdown } from "@/lib/useCountdown";
import {
  awaitsClock,
  isLockedSubmitted,
  markPrefetched,
  resetOverwritesSavedCode,
  resetSaveIsProvisional,
  takePrefetched,
} from "@/lib/questionView";
import { shouldSaveBeforeDeadline } from "@/lib/preDeadlineSave";
import { submitSummary } from "@/lib/submitConfirm";
import { cn, formatHMS } from "@/lib/utils";
import { pausedStateOf } from "./Paused";
import {
  candidatePictureSrc,
  pictureFrom,
  reservedPictureSrc,
} from "@/components/QuestionPrompt";


// Hidden per-question budget for GLOBAL-timed tests. Auto-submits & advances
// when it expires. Intentionally not surfaced in the UI — keeps candidates from
// racing the clock. Hardcoded by design (global mode has no per-question config).
// DISABLED for per_section + per_question attempts (MVP 3.0): both set
// data.section and are paced by the visible per-unit countdown, so this hidden
// force-advance would fight them. (per_question DOES have recruiter-set,
// server-enforced per-question limits — a separate, visible mechanism.)
const QUESTION_TIME_LIMIT_MS = 5 * 60 * 1000;

export default function Question() {
  const { token = "demo", n = "1" } = useParams<{ token: string; n: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const position = Number(n);

  const attemptId = loadAttemptId(token);

  useEffect(() => {
    if (!attemptId) {
      navigate(`/t/${token}`, { replace: true, state: { from: "bounce" } });
    }
  }, [attemptId, navigate, token]);

  const { data, isLoading, error } = useQuery<QuestionResponse>({
    queryKey: queryKeys.question(token, attemptId!, position),
    enabled: Boolean(attemptId) && Number.isInteger(position) && position > 0,
    queryFn: () => fetchQuestion(token, position, attemptId!),
  });

  // Build B: the latest payload, for the closures the anti-cheat controller
  // holds across renders (the question id it tags events with, the snapshot
  // schedule the camera monitor reads lazily).
  const dataRef = useRef<QuestionResponse | undefined>(undefined);
  dataRef.current = data;
  // The payload is the authority; until it lands, the copy the Gate cached at
  // /start — the same frozen schedule — so the attempt's first photo comes
  // when the running test first shows, not when question 1 has rendered
  // (seconds later while the face model warms up the GPU).
  const snapshotOptions = useMemo(
    () => ({
      getConfig: () =>
        dataRef.current
          ? (dataRef.current.camera_snapshots ?? null)
          : (loadCandidateMeta(token)?.camera_snapshots ?? null),
    }),
    [token],
  );

  // FEAT-25: the checks this attempt froze. The question payload is the
  // authority; until it lands, the copy the Gate cached at /start lets a
  // monitored test arm at mount exactly as before. With neither (an attempt
  // whose cache predates the feature) nothing is armed until the payload says
  // what to arm — a camera prompt on a camera-off test is the one thing that
  // must never happen on a guess.
  const cachedAnticheat = useMemo(
    () => loadCandidateMeta(token)?.anticheat,
    [token],
  );
  // FEAT-38: the Gate cached /preview's interaction-context answer at
  // /start. Absent (a meta from before the feature) reads as off — the
  // fold is additive context, never something to arm on a guess.
  const cachedInteraction = useMemo(
    () => loadCandidateMeta(token)?.interaction_context === true,
    [token],
  );
  const anticheat = data
    ? resolveAnticheat(data.anticheat)
    : cachedAnticheat
      ? resolveAnticheat(cachedAnticheat)
      : null;
  const fullscreenOn = anticheat?.fullscreen === true;

  // EF-8 (photo cadence): Review / SectionReview hold the attempt's one 30 s
  // snapshot schedule and read the frozen config from the tab's meta — GET
  // /review carries none. The Gate wrote it at /start; refresh it from the
  // authority here so a meta written before it existed still gets one.
  useEffect(() => {
    if (!data) return;
    const meta = loadCandidateMeta(token);
    if (!meta) return;
    const next = data.camera_snapshots ?? null;
    if (JSON.stringify(meta.camera_snapshots ?? null) === JSON.stringify(next)) return;
    saveCandidateMeta(token, { ...meta, camera_snapshots: next });
  }, [token, data]);

  const section = data?.section ?? null;
  // per_question mode synthesizes one single-question "section" per question on
  // the server, so the section block is present in BOTH locked modes. This flag
  // separates per_question UX (no review screen, auto-advance, whole-test
  // counter, no section sub-header) from per_section UX.
  const perQuestion = data?.timing_mode === "per_question";
  // FEAT-15: per_question has no review screen, so on its last unit the
  // primary button IS the final submit — the one place this screen ends the
  // attempt on a click. (`is_last` is already false when a questionnaire
  // follows, FEAT-11: that click hands the candidate on, it submits nothing.)
  const isFinalSubmit = perQuestion && Boolean(section?.is_last);
  // R14 (pre-ship 2026-10-18): per_question has no review screen, so the
  // OPS-28 #1 guard lives here — this unit's saves the queue gave up on.
  const unitSaveFailures = useAnswerSaveFailures(
    perQuestion && section ? attemptId : null,
    section ? { start: section.start_position, end: section.end_position } : null,
  );

  const [mcqSelection, setMcqSelection] = useState<string | null>(null);
  const [mcqMultiSelection, setMcqMultiSelection] = useState<string[]>([]);
  const [code, setCode] = useState<string>("");
  // B5 — the answer kept in the tab between two saves, so a reload loses
  // nothing (lib/answerDraft.ts). Read ONCE per position, when its payload
  // first lands: the editor mounts with it (Monaco reads `defaultValue`
  // once), the hydration below picks it over saved_*, and the replay records
  // it as one marked step. A LOCKED position never restores (its keep is
  // dropped by the hydration), and neither does a keep the server has moved
  // past: a copy this tab never saw was written on another device.
  const [draftWriter] = useState(createAnswerDraftWriter);
  const restoreRef = useRef<{ position: number; draft: AnswerDraft | null } | null>(
    null,
  );
  if (
    attemptId &&
    data &&
    data.position === position &&
    restoreRef.current?.position !== position
  ) {
    restoreRef.current = {
      position,
      draft: isLockedSubmitted(data)
        ? null
        : loadAnswerDraft(
            attemptId,
            position,
            data.snapshot.type,
            serverAnswerPrint(data),
          ),
    };
  }
  const restoredDraft =
    restoreRef.current?.position === position ? restoreRef.current.draft : null;
  // Every change the candidate makes, written after a short debounce — and
  // at once when the page goes away or the position changes.
  const keepDraft = useCallback(
    (draft: AnswerDraft) => {
      const loaded = dataRef.current;
      if (!attemptId || !loaded || loaded.position !== position) return;
      if (isLockedSubmitted(loaded)) return;
      if (draftKindOf(loaded.snapshot.type) !== draft.kind) return;
      draftWriter.schedule(attemptId, position, draft);
    },
    [attemptId, position, draftWriter],
  );
  useEffect(() => {
    const flush = () => draftWriter.flush();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
    };
  }, [draftWriter, position]);
  // FEAT-31 — the code replay. A recorder exists only for a code question of
  // an attempt that FROZE `code_replay` (the test's switch, the platform and
  // a code question drawn), and never on a submitted position of a
  // forward-only test (nothing typed there can be saved either). It is fed
  // the editor's text through `onCodeChange` — the same string that becomes
  // `final_code` — and samples it every couple of seconds; it never sees a
  // key event. ⚠ Practice and the recruiter's Preview render the same
  // CodeQuestion and must never create one (`codeReplayRecorder.test.ts`).
  const replayRef = useRef<ReplayRecorder | null>(null);
  const replayConfig =
    data &&
    data.position === position &&
    data.snapshot.type === "code" &&
    !isLockedSubmitted(data)
      ? (data.code_replay ?? null)
      : null;
  const replaySampleS = replayConfig?.sample_s ?? null;
  const replayFlushS = replayConfig?.flush_s ?? null;
  // Keyed on PRIMITIVES: `saveCurrent` rewrites the cached `data` on every
  // save, and a recorder re-created on that would open a new session with a
  // keyframe the editor never showed. The initial text is the server's copy;
  // B5: when the editor mounts with a KEPT text instead (`starterCode`
  // below), that is one marked "restore" step on top of it — never typing.
  useEffect(() => {
    if (!attemptId || replaySampleS === null || replayFlushS === null) return;
    const loaded = dataRef.current;
    if (!loaded || loaded.position !== position) return;
    const recorder = createReplayRecorder({
      token,
      attemptId,
      position,
      initialText: loaded.saved_final_code ?? loaded.snapshot.starter_code ?? "",
      sampleS: replaySampleS,
      flushS: replayFlushS,
    });
    const kept =
      restoreRef.current?.position === position ? restoreRef.current.draft : null;
    if (kept?.kind === "code") recorder.restore(kept.code);
    replayRef.current = recorder;
    return () => {
      recorder.dispose();
      if (replayRef.current === recorder) replayRef.current = null;
    };
  }, [token, attemptId, position, replaySampleS, replayFlushS]);
  const onCodeChange = useCallback(
    (value: string) => {
      setCode(value);
      replayRef.current?.setText(value);
      keepDraft({ kind: "code", code: value });
    },
    [keepDraft],
  );
  // What CodeQuestion wires to the editor's own paste / drop events. Stable,
  // and inert while no recorder exists.
  const replayHooks = useMemo(
    () => ({
      beforePaste: () => replayRef.current?.beforePaste(),
      notePaste: () => replayRef.current?.notePaste(),
    }),
    [],
  );
  // EF-10: the prose answer of an open_ended question.
  const [freeText, setFreeText] = useState<string>("");
  // B5: the other answers' change handlers keep them too.
  const onSelectSingle = useCallback(
    (value: string) => {
      setMcqSelection(value);
      keepDraft({ kind: "single", selected: value });
    },
    [keepDraft],
  );
  const onSelectMulti = useCallback(
    (value: string[]) => {
      setMcqMultiSelection(value);
      keepDraft({ kind: "multi", selected: value });
    },
    [keepDraft],
  );
  const onFreeTextChange = useCallback(
    (value: string) => {
      setFreeText(value);
      keepDraft({ kind: "text", text: value });
    },
    [keepDraft],
  );
  // EF-11: the candidate's uploaded file for a file_upload question (the
  // upload route is the save; this mirrors the server's `saved_file`).
  const [savedFile, setSavedFile] = useState<SavedFileInfo | null>(null);
  const [pyodideLoading, setPyodideLoading] = useState(true);
  const [pyodideError, setPyodideError] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<CodeRunResult[] | null>(null);
  // FEAT-12: a language that runs on our servers (C / C++ / Go) — the last
  // run's compile error / refusal, and the request to abort when the
  // candidate moves on (Next, Submit, time-up never wait for a run).
  const [compileError, setCompileError] = useState<string | null>(null);
  const [runFailure, setRunFailure] = useState<RunFailure | null>(null);
  const serverRunRef = useRef<AbortController | null>(null);
  useEffect(() => () => serverRunRef.current?.abort(), [position]);
  // FEAT-29: the candidate's own test cases for THIS question — kept for the
  // tab (they survive Next / Back and a reload), never saved with the answer.
  // Their results are a state of their own: `results` is the example tests
  // only, because that is what `saveCurrent` stores as the answer's visible
  // test results.
  const [ownTests, setOwnTests] = useOwnTests(
    attemptId ? `${attemptId}:${position}` : null,
  );
  const [ownResults, setOwnResults] = useState<OwnTestResult[] | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // "Copy & pasting detected" — the candidate-facing deterrent. Opens on every
  // paste that actually LANDS in the code editor (clipboard or drag-and-drop);
  // page pastes are blocked silently by the controller and earn no notice. A
  // notice, not a block: the controller has already recorded the event and
  // let the text through before this fires. Idempotent — a paste while the
  // notice is up just keeps it up. Resets with the route (Question remounts
  // per position).
  const [pasteNoticeOpen, setPasteNoticeOpen] = useState(false);

  // EF-2 — the Pause confirm dialog (shown only when the attempt froze
  // `pause_enabled`), its in-flight flag and its retryable failure copy.
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [pauseError, setPauseError] = useState<string | null>(null);

  // FEAT-15 — the confirm before the final submit (see `isFinalSubmit`) and
  // its retryable failure copy. The in-flight flag is `submitting`.
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [submitConfirmError, setSubmitConfirmError] = useState<string | null>(
    null,
  );
  // OPS-28 #30: a non-final section advance that failed used to set only the
  // confirm's line — which is not on screen for that advance — so the button
  // simply re-enabled with no message. This one renders under the header.
  // ⚠ Placeholder copy (wording is Emilia's).
  const [advanceError, setAdvanceError] = useState<string | null>(null);
  // OPS-28 #2: the time-up section transition (finish / advance) failed on a
  // live attempt. The candidate stays here with a Retry instead of landing on
  // Done, where /submit answers 409 `sections_remaining` and the remaining
  // sections are never taken.
  const [timeUpError, setTimeUpError] = useState<string | null>(null);

  // EF-2: hand over to the paused screen. Used after our own POST /pause AND
  // whenever any request answers 409 `attempt_paused` (a stale tab, the
  // gate's question-1 retry) — the 409 carries the same facts, so the paused
  // screen never needs router state from anywhere else.
  const goPaused = useCallback(
    (paused: PausedAttempt) => {
      navigate(`/t/${token}/paused`, {
        replace: true,
        state: pausedStateOf(paused),
      });
    },
    [navigate, token],
  );

  // Anti-cheat: install document/window listeners once we have an attempt id;
  // emit `question_viewed` whenever the candidate lands on a new position.
  const anticheatRef = useAntiCheat(
    token,
    attemptId,
    // Build B: tag every event with the frozen question id, so the server can
    // tell an event on a relaxed file_upload question from every other one.
    () => dataRef.current?.question_id ?? undefined,
    (payload) => {
      if (payload.in_editor) setPasteNoticeOpen(true);
    },
    snapshotOptions,
    anticheat
      ? {
          camera: anticheat.camera,
          activity: anticheat.activity,
          interaction: cachedInteraction, // FEAT-38
        }
      : null,
  );
  useEffect(() => {
    if (!attemptId || !data) return;
    anticheatRef.current?.onQuestionChange(position);
  }, [anticheatRef, attemptId, data, position]);

  // FEAT-17: no right-click, no selecting the question content — blocked,
  // never recorded. Lifted on a RELAXED file question (EF-11): the recruiter
  // let the candidate work in another program, so copying sample data out of
  // the prompt is legitimate there; the next question restores it.
  useContentProtection(
    Boolean(attemptId) &&
      !(
        data?.snapshot.type === "file_upload" &&
        data.snapshot.file_upload_rules?.relax_anticheat
      ),
  );

  // Fullscreen state. The candidate enters fullscreen for the first time
  // via the blocking overlay's CTA on Q1 mount (no auto-request on form
  // submit — see Gate.tsx). Every subsequent exit re-shows the overlay
  // until they click "Return to fullscreen mode". The hook emits
  // `fullscreen_exit` on every OS-level release and `fullscreen_unavailable`
  // if the gesture-driven request rejects.
  const {
    isFullscreen: inFullscreen,
    hasEverEntered,
    exitCause: fullscreenExitCause,
    isSupported: fullscreenSupported,
    enter: enterFullscreen,
  } = useFullscreen(anticheatRef, Boolean(attemptId) && fullscreenOn);

  // Browsers without the Fullscreen API skip the blocking overlay entirely
  // (we don't want to hard-lock an unsupported browser out of the test).
  // Log it once on the first question mount so the recruiter still sees
  // the attempt happened without fullscreen enforcement. Sentinel ref
  // guards against React-strict double-invocation + re-mount on data load.
  const unsupportedLoggedRef = useRef(false);
  useEffect(() => {
    if (!attemptId || !fullscreenOn || fullscreenSupported || position !== 1) {
      return;
    }
    if (unsupportedLoggedRef.current) return;
    unsupportedLoggedRef.current = true;
    emitEvent(token, attemptId, {
      type: "fullscreen_unavailable",
      reason: "not_supported",
    });
  }, [token, attemptId, fullscreenOn, fullscreenSupported, position]);

  // Hydrate the editor/selection from the candidate's OWN previously-saved
  // answer (returned by /question) — one-shot per position so a background
  // refetch can't clobber in-progress edits, and only when the loaded data is
  // actually for the current position. This makes within-section back-nav and
  // the per-section review's jump-back show the prior answer instead of a
  // blank question (which would otherwise let a re-submit overwrite good code
  // with starter text).
  // Load (or reload) the Pyodide runtime for a code question, tracking the
  // loading + error flags. A failed load no longer bricks the attempt: the
  // singleton self-heals (lib/pyodide.ts clears its cache on rejection), so
  // Retry — or the next Run click — tries afresh. The candidate can keep
  // editing meanwhile; their code is graded server-side regardless.
  //
  // FEAT-6: the runtime is the one for THIS question's language (the
  // recruiter's choice, frozen in the snapshot) — Pyodide, QuickJS or sql.js.
  // The `pyodide*` state names predate that; they mean "the runtime".
  const codeLanguage =
    data?.snapshot.type === "code"
      ? languageOf(data.snapshot.allowed_languages)
      : "python";
  const loadPyodideRuntime = useCallback(() => {
    setPyodideError(false);
    setPyodideLoading(true);
    void getRuntime(codeLanguage)
      .then(() => setPyodideLoading(false))
      .catch(() => {
        setPyodideLoading(false);
        setPyodideError(true);
      });
  }, [codeLanguage]);

  const hydratedPositionRef = useRef<number | null>(null);
  useEffect(() => {
    if (!data || data.position !== position) return;
    if (hydratedPositionRef.current === position) return;
    hydratedPositionRef.current = position;
    // B5: a keep wins over saved_* — it is dropped once a save catches up, so
    // one that exists is newer. A locked position shows the server's copy and
    // its keep goes (`restoredDraft` is already null there).
    if (attemptId && isLockedSubmitted(data)) clearAnswerDraft(attemptId, position);
    const kept = restoredDraft;
    setMcqSelection(
      kept?.kind === "single" ? kept.selected : (data.saved_selected_answer ?? null),
    );
    setMcqMultiSelection(
      kept?.kind === "multi" ? kept.selected : (data.saved_selected_answers ?? []),
    );
    setFreeText(kept?.kind === "text" ? kept.text : (data.saved_free_text ?? ""));
    setSavedFile(data.saved_file ?? null);
    setResults(null);
    setOwnResults(null);
    setCompileError(null);
    setRunFailure(null);
    if (data.snapshot.type === "code") {
      setCode(
        kept?.kind === "code"
          ? kept.code
          : (data.saved_final_code ?? data.snapshot.starter_code ?? ""),
      );
      loadPyodideRuntime();
    } else {
      setPyodideLoading(false);
      setPyodideError(false);
    }
  }, [attemptId, data, position, loadPyodideRuntime, restoredDraft]);

  // Prefetch question N+1 the moment N is loaded, so a forward "Next" renders
  // from cache with no GET on the critical path (the other half of the instant-
  // navigation fix — the enqueued save removes the POST leg). Only when N+1 is
  // reachable right now: global (any next position ≤ total) or WITHIN the active
  // per_section section (next ≤ its end_position). Skipped for per_question (the
  // next unit sits in a still-locked section — GET would 409) and at a section
  // boundary (the next screen is a review, not a question). The warm-up asks
  // the server NOT to stamp answer.started_at — that is the start of the
  // per-question time the recruiter sees, and stamping it here charged N's
  // thinking time to N+1 (#122); the arrival effect below starts the clock.
  useEffect(() => {
    if (!data || !attemptId || data.position !== position) return;
    if (data.timing_mode === "per_question") return;
    // EF-9: never on a statement. The prefetch GET stamps the next
    // statement's `started_at` while the candidate is still reading this one,
    // which would inflate every response time and hide a speed-clicker from
    // the "answered too fast" flag. One small GET on arrival is the price.
    if (data.untimed) return;
    const nextPos = position + 1;
    // FEAT-11: and never ACROSS the boundary either. `untimed` is per
    // position now, so the last technical question is timed and would happily
    // prefetch statement 1 — stamping it while the candidate is still on the
    // coding question, which is exactly the corruption the rule above exists
    // to prevent.
    if (
      data.questionnaire_starts_at != null &&
      nextPos >= data.questionnaire_starts_at
    )
      return;
    const withinReach = data.section
      ? nextPos <= data.section.end_position
      : nextPos <= data.total;
    if (!withinReach) return;
    markPrefetched(attemptId, nextPos);
    void queryClient.prefetchQuery({
      queryKey: queryKeys.question(token, attemptId, nextPos),
      queryFn: () =>
        fetchQuestion(token, nextPos, attemptId, { prefetch: true }),
      staleTime: 30_000,
    });
  }, [data, position, attemptId, token, queryClient]);

  // #122: a position the prefetch warmed renders from cache and never sent a
  // stamping GET — start its clock now that the candidate is actually on it.
  // Fire-and-forget: the server stamps only an unset started_at. The position
  // is remembered while that GET is in flight, because the question's picture
  // must not be asked for before it lands (see `awaitsClock`).
  const [stampingPosition, setStampingPosition] = useState<number | null>(null);
  useEffect(() => {
    if (!data || !attemptId || data.position !== position) return;
    if (!takePrefetched(attemptId, position)) return;
    setStampingPosition(position);
    // OPS-28 #30: a stamp that fails is a clock that never started — the
    // candidate gains time and the "answered too fast" flag reads off a later
    // start. One more try after a beat (apiFetch already retried 502/503/504
    // on the GET), then Sentry hears it: the position only, never the answer.
    void fetchQuestion(token, position, attemptId)
      .catch(
        () =>
          new Promise<void>((r) => setTimeout(r, 2000)).then(() =>
            fetchQuestion(token, position, attemptId),
          ),
      )
      .catch((err: unknown) =>
        captureSilentFailure("question_clock_stamp_failed", {
          position,
          status: err instanceof ApiError ? err.status : "network",
        }),
      )
      .finally(() =>
        setStampingPosition((cur) => (cur === position ? null : cur)),
      );
  }, [data, position, attemptId, token]);

  // FEAT-12 (spec §3.5): the question's frozen example tests run on our
  // servers. A late answer (the candidate moved on) is discarded; a refusal
  // keeps the last completed results on screen.
  const runOnServer = async () => {
    if (!data || data.snapshot.type !== "code" || !attemptId) return;
    serverRunRef.current?.abort();
    const controller = new AbortController();
    serverRunRef.current = controller;
    setRunning(true);
    setCompileError(null);
    setRunFailure(null);
    // FEAT-29: the own cases go in the SAME Run — one job, one against the
    // throttle. Captured now: the rows are locked while the Run is in flight,
    // and the results name the inputs they ran with.
    const ownSent = ownTests;
    try {
      const outcome = await runOnServerAsCandidate(
        { token, attemptId, position, code, ownTests: ownTestsPayload(ownSent) },
        controller.signal,
      );
      if (outcome === null || controller.signal.aborted) return;
      setRunFailure(outcome.failure);
      setCompileError(outcome.compileError);
      if (outcome.failure === null) {
        // FEAT-31: a Run that reached the runner shows on the replay.
        replayRef.current?.mark("run", {
          p:
            outcome.compileError === null
              ? outcome.results.filter((r) => r.passed).length
              : 0,
          t: data.snapshot.example_tests?.length ?? 0,
        });
        setResults(outcome.compileError === null ? outcome.results : null);
        setOwnResults(
          outcome.compileError === null
            ? attachOwnResults(ownSent, outcome.ownRows)
            : null,
        );
      }
    } finally {
      if (serverRunRef.current === controller) setRunning(false);
    }
  };

  const onRun = async () => {
    if (isServerLanguage(codeLanguage)) return runOnServer();
    if (!data || data.snapshot.type !== "code") return;
    setRunning(true);
    // Make sure the runtime is ready first. A load failure surfaces the friendly
    // "sandbox didn't load — Retry" banner (getPyodide self-heals on retry)
    // instead of a cryptic single "runner" error buried in the results list.
    try {
      await getRuntime(codeLanguage);
    } catch {
      setPyodideError(true);
      setResults(null);
      setOwnResults(null);
      setRunning(false);
      return;
    }
    setPyodideError(false);
    // FEAT-18: a Run in the browser is recorded on the server too, as the
    // runner languages' Runs are. Only once code actually ran — a sandbox
    // that never loaded (the return above) ran nothing. Fire-and-forget: the
    // log never delays or fails a Run.
    const startedAt = performance.now();
    const logRun = (passed: boolean) => {
      if (!attemptId) return;
      logBrowserRun(token, attemptId, position, {
        passed,
        wallMs: performance.now() - startedAt,
      });
    };
    try {
      const tests = data.snapshot.example_tests ?? [];
      const fnName = detectFunctionName(codeLanguage, data.snapshot.starter_code, code);
      const out: CodeRunResult[] = [];
      for (const t of tests) {
        const r = await runUserCodeAgainstTest(codeLanguage, {
          code,
          testInput: t.input,
          expectedOutput: t.expected_output,
          functionName: fnName,
          setupCode: data.snapshot.setup_code,
        });
        out.push({
          name: t.name,
          passed: r.passed,
          output: r.output,
          error: r.error,
        });
      }
      // FEAT-29: then the candidate's own cases, in the tab, by the same
      // engine. They never join `out` — the log below and the saved visible
      // results keep meaning "the example tests".
      const own = await runOwnTestsInBrowser(codeLanguage, {
        code,
        functionName: fnName,
        setupCode: data.snapshot.setup_code,
        contract: questionContract(codeLanguage, data.snapshot.function_signature),
        tests: ownTests,
      });
      setResults(out);
      setOwnResults(own);
      logRun(out.every((r) => r.passed));
      replayRef.current?.mark("run", {
        p: out.filter((r) => r.passed).length,
        t: out.length,
      });
    } catch (e) {
      setResults([
        {
          name: "runner",
          passed: false,
          error: e instanceof Error ? e.message : String(e),
        },
      ]);
      setOwnResults(null);
      logRun(false);
      replayRef.current?.mark("run", {
        p: 0,
        t: data.snapshot.example_tests?.length ?? 0,
      });
    } finally {
      setRunning(false);
    }
  };

  // Non-blocking save of the current question's answer. Shared by the
  // next/back/finish actions and the timer-driven transitions. The durable POST
  // is ENQUEUED (serialized, retried — see answerQueue.ts) so navigation never
  // waits on it; the local cache is updated synchronously so back-nav / Review
  // reflect the edit instantly. Callers that need the write to have LANDED
  // (submit, section advance) await flushAnswerSaves() first.
  //
  // FEAT-18: `code` saves THAT code instead of the editor state — the reset
  // to the starter, called in the same tick as the edit, before `code` state
  // has caught up. The old code's Run results never ride along with it.
  const saveCurrent = useCallback((opts: { provisional?: boolean; code?: string } = {}) => {
    if (!data || !attemptId) return;
    if (data.snapshot.type === "file_upload") {
      // EF-11: the upload IS the save (its own route, already landed by the
      // time the candidate can move on); /answer must not be called with an
      // empty code body. Only the Review pills need refreshing.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.review(token, attemptId),
      });
      return;
    }
    // Snapshot the answer BY VALUE now, so the queued POST persists exactly what
    // was on screen at click time even though state moves on after we navigate.
    const savePosition = position;
    const saveAttemptId = attemptId;
    const saveToken = token;
    const savedSelected = mcqSelection ?? undefined;
    const savedMulti = mcqMultiSelection;
    const savedCode = opts.code ?? code;
    const savedFreeText = freeText;
    const savedResults =
      opts.code === undefined && results
        ? results.map((r) => ({ name: r.name, passed: r.passed }))
        : undefined;
    // The question types differ only in the request body; the enqueue +
    // submit scaffold is identical. true_false is mcq-shaped on the wire.
    // EF-9: likert is single-select too — its value is "1"–"5".
    const payload =
      data.snapshot.type === "mcq" ||
      data.snapshot.type === "true_false" ||
      data.snapshot.type === "likert"
        ? { selected_answer: savedSelected }
        : data.snapshot.type === "mcq_multi"
          ? { selected_answers: savedMulti }
          : data.snapshot.type === "open_ended"
            ? { free_text: savedFreeText }
            : { final_code: savedCode, final_test_results: savedResults };
    // B5: the keep holds what is being saved until the save LANDS — a reload
    // while it is queued restores it — and then goes, unless the candidate
    // has changed the answer again meanwhile (that keep waits for its own).
    const savedDraft: AnswerDraft =
      "selected_answer" in payload
        ? { kind: "single", selected: mcqSelection }
        : "selected_answers" in payload
          ? { kind: "multi", selected: savedMulti }
          : "free_text" in payload
            ? { kind: "text", text: savedFreeText }
            : { kind: "code", code: savedCode };
    draftWriter.cancel();
    if (!isLockedSubmitted(data)) {
      saveAnswerDraft(saveAttemptId, savePosition, savedDraft);
    }
    // ...and the server may hold it from here on: a keep over it stays ours.
    noteServerAnswer(saveAttemptId, savePosition, answerPrint(savedDraft));
    // OPS-28 #1: named, so a save abandoned after its retries is KEPT (and
    // the review screens hold the Submit) instead of vanishing behind the
    // cache-sync below, which shows it as saved either way. With its bytes,
    // so a re-save of an answer the server refused stays let go (R13).
    enqueueAnswerSave(
      async (signal) => {
        const saved = await submitAnswer(
          saveToken,
          savePosition,
          saveAttemptId,
          payload,
          signal,
          { provisional: opts.provisional },
        );
        clearAnswerDraft(saveAttemptId, savePosition, savedDraft);
        return saved;
      },
      { attemptId: saveAttemptId, position: savePosition, payloadPrint: JSON.stringify(payload) },
    );
    // Keep the cached question response in sync with what we just enqueued, so a
    // later Back / Review jump re-hydrates the candidate's OWN answer instead of
    // the pre-answer snapshot this GET was first cached with (staleTime 30s).
    // Without this, returning to a visited question reads the stale cache and the
    // hydration effect wipes the live selection back to its pre-answer value.
    queryClient.setQueryData<QuestionResponse>(
      queryKeys.question(token, attemptId, position),
      (old) =>
        old
          ? {
              ...old,
              saved_selected_answer: mcqSelection ?? null,
              saved_selected_answers: mcqMultiSelection,
              saved_final_code: savedCode,
              saved_free_text: freeText,
            }
          : old,
    );
    // Review pills (answered/unanswered) read a separately-cached query — mark it
    // stale so the Review screen refetches. Review/SectionReview also flush the
    // save queue on mount before enabling that query, so the refetched pills
    // reflect the just-enqueued save even if its POST hasn't landed yet.
    // The 2-arg key is deliberate: SectionReview's entries are section-scoped
    // (queryKeys.review's third element, BUG-5), and react-query matches by
    // prefix — so this one call still invalidates every section's entry plus the
    // global one, with nothing to keep in sync here.
    void queryClient.invalidateQueries({
      queryKey: queryKeys.review(token, attemptId),
    });
  }, [
    attemptId,
    code,
    data,
    draftWriter,
    freeText,
    mcqMultiSelection,
    mcqSelection,
    position,
    queryClient,
    results,
    token,
  ]);

  // FEAT-18 — the editor was reset to the starter (CodeQuestion made the edit;
  // `code` state follows through `onChange`). Whatever the old code left on
  // screen goes with it.
  //
  // ⚠ The server's copy has to go too, but only when there is one — the rule
  // and its two traps are `resetOverwritesSavedCode` (lib/questionView.ts).
  const onResetCode = useCallback(() => {
    // FEAT-31: `onChange` has already handed the recorder the starter.
    replayRef.current?.mark("reset");
    // B5: the pre-reset code must not come back on a reload.
    draftWriter.cancel();
    if (attemptId) clearAnswerDraft(attemptId, position);
    serverRunRef.current?.abort();
    setResults(null);
    setOwnResults(null); // FEAT-29: the old code's own-case results go too
    setCompileError(null);
    setRunFailure(null);
    if (!data || data.snapshot.type !== "code") return;
    if (!resetOverwritesSavedCode(data)) return;
    saveCurrent({
      code: data.snapshot.starter_code ?? "",
      provisional: resetSaveIsProvisional(data),
    });
  }, [attemptId, data, draftWriter, position, saveCurrent]);

  // FEAT-18 — the help panel's "How the code editor works": the workspace's
  // own text or the platform default, i.e. what the start page showed. It
  // rides on `/preview`, not on the question, so it is read only once Help is
  // opened — usually straight from the Gate's cache (never refetched: it
  // cannot change mid-test for the candidate's purposes, and `/preview` also
  // wakes the code runner's pools). A failure just leaves the built-in text.
  const [helpWanted, setHelpWanted] = useState(false);
  const previewQuery = useQuery({
    queryKey: queryKeys.preview(token),
    queryFn: () => fetchPreview(token),
    enabled: helpWanted,
    staleTime: Infinity,
    retry: false,
  });

  // Single latch for "the active deadline has lapsed and we've handled it" —
  // reset whenever expires_at changes (i.e. on each section transition while
  // this component stays mounted; never during a non-sectioned attempt).
  const timeUpHandledRef = useRef(false);
  // Same lifetime as the latch above: one pre-deadline save per deadline.
  const preDeadlineSavedRef = useRef(false);

  // Drive the end-of-section / end-of-attempt transition when time runs out
  // (no review screen — the clock decided). Used by both the countdown effect
  // and the answer-save error path (a save that races past the deadline).
  const handleTimeUp = useCallback(async () => {
    if (!data || !attemptId || timeUpHandledRef.current) return;
    timeUpHandledRef.current = true;
    // Enqueue the current answer, then wait for the queue to drain so this
    // section's writes are durable before we submit/advance (best-effort: a save
    // that races past the deadline is abandoned, and the transition proceeds).
    saveCurrent();
    // FEAT-31: cut the replay now so its last chunk rides alongside the save
    // (bounded at 1.5 s, never throws — it cannot hold the transition up).
    const replayFlushed = flushReplayUploads();
    await flushAnswerSaves().catch(() => {});
    await replayFlushed;
    // FEAT-11 — on a MIXED attempt the clock that lapsed is the TECHNICAL
    // half's, and a clockless questionnaire is still owed. Hand the candidate
    // on instead of submitting (Leo, 2026-09-17): the recruiter sent one test
    // to get two results, and a slow coder must not cost them the profile.
    // Checked before the section branches, because it applies in every timing
    // mode — including the last section and a shared clock, where lapsing
    // would otherwise be terminal. The server agrees: it answers a technical
    // write with the NON-terminal 410 `technical_time_up`.
    if (
      data.questionnaire_starts_at != null &&
      position < data.questionnaire_starts_at
    ) {
      navigate(`/t/${token}/question/${data.questionnaire_starts_at}`, {
        replace: true,
      });
      return;
    }
    const sec = data.section;
    if (!sec) {
      navigate(`/t/${token}/done`, { replace: true });
      return;
    }
    // Last section — or, under a shared clock (CP-18), ANY section: the
    // deadline that lapsed is the whole test's, so the attempt is over.
    if (sec.is_last || data.shared_clock) {
      try {
        await submitAttempt(token, attemptId);
      } catch {
        // Already-submitted / terminal — Done handles it.
      }
      navigate(`/t/${token}/done`, { replace: true });
    } else {
      // OPS-28 #2: the section is over but the test is not. Until 2026-10-17
      // ANY failure here (`catch {}`) sent the candidate to Done, where the
      // safety-net /submit answers 409 `sections_remaining` and the rest of
      // the test was never taken. Now classified like `advance`: a network
      // throw gets one more try; a server that converged the attempt (done /
      // paused / gone) is followed; anything else keeps the candidate HERE
      // with a Retry, and the latch is released so that Retry can run again.
      const closeSection = async (): Promise<void> => {
        if (data.break_between_sections) {
          // CP-18: close it into a BREAK (server-stamped) and let the
          // candidate begin the next section when they are ready — never
          // start its clock for them. No `expiresAt` in the handed-over
          // state: the break screen must not tick a frozen deadline.
          await finishSection(token, attemptId, sec.index);
          navigate(`/t/${token}/section-review`, {
            replace: true,
            state: {
              sectionStart: sec.start_position,
              sectionEnd: sec.end_position,
              sectionIndex: sec.index,
              isLast: sec.is_last,
              paused: true,
              sharedClock: data.shared_clock,
              breakBetweenSections: true,
            },
          });
        } else {
          const resp = await advanceSection(token, attemptId, sec.index);
          navigate(`/t/${token}/question/${resp.first_position}`, {
            replace: true,
          });
        }
      };
      let failure: unknown = null;
      try {
        await closeSection();
        return;
      } catch (e) {
        failure = e;
      }
      if (!(failure instanceof ApiError)) {
        // A blip, not a verdict: once more, after a beat.
        await new Promise<void>((r) => setTimeout(r, 1500));
        try {
          await closeSection();
          return;
        } catch (e) {
          failure = e;
        }
      }
      const pausedNow = pausedAttemptOf(failure);
      if (pausedNow) {
        goPaused(pausedNow);
        return;
      }
      const action = classifyAdvanceError(failure);
      if (action === "done") {
        navigate(`/t/${token}/done`, { replace: true });
        return;
      }
      if (action === "clear-and-bounce") {
        bounceToGate(token, navigate);
        return;
      }
      timeUpHandledRef.current = false;
      setTimeUpError(TIME_UP_CLOSE_FAILED_MESSAGE);
    }
  }, [attemptId, data, goPaused, navigate, saveCurrent, token]);

  // Whether the candidate has supplied any answer for THIS question. ONE call
  // for every reader — `advance` (the Required gate + whether to save), the
  // Required button's `disabled` + the Skip-vs-Save/Submit label, and the
  // pre-deadline save. B9 moved code to "not blank and not the untouched
  // starter", and a second inline copy kept "non-empty": the button stayed
  // enabled on the starter while `advance` refused the click silently.
  const isAnswered = data
    ? hasAnswerFor(data.snapshot.type, {
        mcqSelection,
        mcqMultiSelection,
        freeText,
        savedFile,
        code,
        // B9: the editor opens on the starter — untouched is not an answer.
        starterCode: data.snapshot.starter_code,
      })
    : false;

  const advance = useCallback(
    async (force: boolean) => {
      if (!data || !attemptId) return;
      const answered = isAnswered;
      // Required gate (all modes): block a manual advance past a Required,
      // unanswered question. Timer-driven force-advance (force=true) bypasses
      // it so a candidate is never trapped against the clock.
      if (!force && data.answer_required && !answered) return;

      setSubmitting(true);
      try {
        // Persist only when there's an answer. Skipping an optional question —
        // or a timer force-advancing a blank one — leaves submitted_at null so
        // the question stays returnable under free navigation. The save is
        // ENQUEUED, not awaited: the plain within-test "Next" (global + within a
        // per_section section) navigates immediately with no network on the
        // critical path (prefetch already warmed the next question).
        // #112: a submitted position on a forward-only test is read-only —
        // the server would 409 the save and the queue would swallow it.
        if (answered && !isLockedSubmitted(data)) saveCurrent();
        const sec = data.section;
        if (sec && perQuestion) {
          // per_question: each question is its own timed unit — no review
          // screen. Last unit → submit + done; otherwise advance to the next
          // unit's question (the same transition the per-question clock drives).
          // Both cross a server boundary that reads persisted answers (submit) or
          // locks the current unit (advance), so drain the save queue first.
          const replayFlushed = flushReplayUploads(); // FEAT-31, bounded
          await flushAnswerSaves();
          await replayFlushed;
          // R14 (pre-ship 2026-10-18): a save of this unit the queue gave up
          // on — submit / advance would grade or lock it blank while the
          // screen says answered. A manual move stops here; the note under the
          // header offers the Retry. The clock-driven move (force) goes on:
          // the clock decided, as on the review screens.
          if (
            !force &&
            answerSaveFailures(attemptId).some(
              (f) => f.position >= sec.start_position && f.position <= sec.end_position,
            )
          ) {
            setSubmitConfirmOpen(false);
            return;
          }
          if (sec.is_last) {
            await submitAttempt(token, attemptId);
            navigate(`/t/${token}/done`, { replace: true });
          } else {
            const resp = await advanceSection(token, attemptId, sec.index);
            navigate(`/t/${token}/question/${resp.first_position}`, {
              replace: true,
            });
          }
        } else if (sec) {
          // per_section: advance within the section's range; at the last
          // question of the section, hand off to the mandatory per-section
          // review (which then advances or submits).
          if (position >= sec.end_position) {
            navigate(`/t/${token}/section-review`, {
              replace: true,
              state: {
                sectionStart: sec.start_position,
                sectionEnd: sec.end_position,
                sectionIndex: sec.index,
                isLast: sec.is_last,
                // BUG-5: hand this section's authoritative deadline over to the
                // review screen, so its countdown renders on the first frame
                // instead of waiting on GET /review (and can't re-base when that
                // lands). Safe because attempt.expires_at only moves at /start
                // and /advance-section — it cannot change while the review is up.
                expiresAt: data.expires_at,
                // CP-18: lets the review screen's time-up effect choose finish /
                // submit / advance BEFORE GET /review resolves.
                sharedClock: data.shared_clock,
                breakBetweenSections: data.break_between_sections,
              },
            });
          } else {
            navigate(`/t/${token}/question/${position + 1}`, { replace: true });
          }
        } else {
          const isLast = position >= data.total;
          navigate(
            isLast
              ? `/t/${token}/review`
              : `/t/${token}/question/${position + 1}`,
            { replace: true },
          );
        }
      } catch (e) {
        // NB: this catch never sees a failed POST /answer — saveCurrent()
        // ENQUEUES, and answerQueue swallows a permanently-failed save so the
        // chain keeps draining (flushAnswerSaves() therefore never rejects).
        // What lands here is the awaited transition: submitAttempt /
        // advanceSection.
        // EF-2: paused meanwhile (another tab pressed Pause) — the paused
        // screen, rebuilt from the 409; the attempt is very much alive.
        const pausedNow = pausedAttemptOf(e);
        if (pausedNow) {
          goPaused(pausedNow);
          return;
        }
        const action = classifyAdvanceError(e);
        // Kept though currently unreachable: only POST /answer can raise the
        // non-terminal section_time_up. See classifyAdvanceError for why it is
        // deliberately not deleted.
        if (action === "section-transition") {
          if (data.section) {
            await handleTimeUp();
            return;
          }
          // Unreachable by construction — only the sectioned branch of
          // _enforce_deadline raises section_time_up — but if the server ever
          // says it for a non-sectioned attempt there is no section to move to,
          // so converge to Done rather than silently swallowing the failure.
          navigate(`/t/${token}/done`, { replace: true });
          return;
        }
        // If the server says this attempt is already finalized (409/410), there
        // is nothing more to do — push the candidate to the Done page.
        if (action === "done") {
          navigate(`/t/${token}/done`, { replace: true });
          return;
        }
        // 10-07 S11: voided (an invitation replaced this link attempt, or a
        // retake) or erased — no Submit can ever succeed. Same exit as the
        // question fetch's 404: drop the id, back to the Gate.
        if (action === "clear-and-bounce") {
          bounceToGate(token, navigate);
          return;
        }
        // "stay": a network blip, any other 404, a 422 or a 5xx. Leave them on the question
        // so the answer can be retried; the queue handles its own retries.
        // FEAT-15: the confirm is the only place its line is rendered — so a
        // failed NON-final advance (a section lock) says so under the header
        // instead (OPS-28 #30), where before the button just re-enabled.
        if (isFinalSubmit) setSubmitConfirmError(SUBMIT_FAILED_MESSAGE);
        else setAdvanceError(ADVANCE_FAILED_MESSAGE);
      } finally {
        setSubmitting(false);
      }
    },
    [
      attemptId,
      data,
      goPaused,
      handleTimeUp,
      isAnswered,
      isFinalSubmit,
      navigate,
      perQuestion,
      position,
      saveCurrent,
      token,
    ],
  );

  // FEAT-15: the primary button's click. The final submit asks first; every
  // other advance goes straight through. Opening the confirm deliberately
  // saves NOTHING: a save finalizes a forward-only position (#112), and the
  // candidate who then pressed "Not yet" and changed the answer would have
  // that change refused by the server and swallowed by the queue. `advance`
  // saves it on confirm, exactly as it always has.
  const requestAdvance = useCallback(() => {
    setAdvanceError(null);
    if (isFinalSubmit) {
      setSubmitConfirmError(null);
      setSubmitConfirmOpen(true);
      return;
    }
    void advance(false);
  }, [advance, isFinalSubmit]);

  // FEAT-15: "N of M answered" for that confirm. This screen knows only its
  // own question, so the rest comes from GET /review — fetched fresh each time
  // the dialog opens, after the queue has landed the EARLIER positions' saves.
  // The on-screen answer is not on the server yet; `submitSummary` takes it
  // from local state instead. Never a gate: the dialog's Submit stays enabled
  // while this loads and if it fails (the per-question clock is running).
  const submitSummaryQuery = useQuery({
    queryKey: queryKeys.submitSummary(token, attemptId),
    enabled: submitConfirmOpen && Boolean(attemptId),
    queryFn: async () => {
      await flushAnswerSaves();
      return fetchReview(token, attemptId!);
    },
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  // Step back one position. Allowed only when the test permits back navigation
  // (free-nav global, or within a section in per_section). Lower bound is the
  // section start when sectioned, else question 1. Best-effort save first so an
  // in-progress edit isn't lost.
  const goBack = useCallback(() => {
    if (!attemptId || !data || !data.allow_back_navigation) return;
    const lowerBound = data.section ? data.section.start_position : 1;
    if (position <= lowerBound) return;
    // Enqueue the current answer (the queue persists it in the background) and
    // step back immediately — the target position renders from cache, which
    // saveCurrent just synced, so the prior answer shows without a round-trip.
    saveCurrent();
    navigate(`/t/${token}/question/${position - 1}`, { replace: true });
  }, [attemptId, data, navigate, position, saveCurrent, token]);

  // Jump to the Review overview at any time (free-nav global only). Enqueue the
  // current edit (Review flushes the queue on mount before showing the pills) and
  // navigate immediately.
  const goReview = useCallback(() => {
    if (!attemptId) return;
    saveCurrent();
    navigate(`/t/${token}/review`);
  }, [attemptId, navigate, saveCurrent, token]);

  // EF-2: pause the test on THIS question. The current answer is enqueued and
  // the queue DRAINED before /pause — a save landing after the pause is
  // refused (409 attempt_paused) and dropped, exactly as with a CP-18 break.
  // The Paused screen it hands over to unmounts every anti-cheat listener
  // with this route, disposes the camera and exits fullscreen, so a pause
  // emits no tab / fullscreen / camera events by construction.
  const pauseNow = useCallback(async () => {
    if (!data || !attemptId) return;
    setPausing(true);
    setPauseError(null);
    try {
      saveCurrent();
      // FEAT-31: a replay chunk landing after the pause is refused (409).
      const replayFlushed = flushReplayUploads();
      await flushAnswerSaves();
      await replayFlushed;
      const resp = await pauseAttempt(token, attemptId, position);
      goPaused({
        position: resp.position,
        paused_at: resp.paused_at,
        resume_by: resp.resume_by,
        remaining_seconds: resp.remaining_seconds,
      });
    } catch (e) {
      // Already paused (a double click, another tab) → the paused screen.
      const already = pausedAttemptOf(e);
      if (already) {
        goPaused(already);
        return;
      }
      // Finalized / expired → Done; anything else stays on the question with
      // the dialog open, so the candidate can try again.
      const pauseAction = classifyAdvanceError(e);
      if (pauseAction === "done") {
        navigate(`/t/${token}/done`, { replace: true });
        return;
      }
      // 10-07 S11: the attempt is gone (voided / erased) — back to the Gate.
      if (pauseAction === "clear-and-bounce") {
        bounceToGate(token, navigate);
        return;
      }
      setPauseError("Could not pause the test. Please try again.");
    } finally {
      setPausing(false);
    }
  }, [attemptId, data, goPaused, navigate, position, saveCurrent, token]);

  // Keep a ref to the latest `advance` so the hidden timer can call the
  // current closure (with up-to-date code/selection) without resetting on
  // every keystroke.
  const advanceRef = useRef(advance);
  useEffect(() => {
    advanceRef.current = advance;
  }, [advance]);

  // Hidden per-question timer — global attempts only. per_section + per_question
  // both set data.section and are governed by the visible per-unit countdown,
  // so this hardcoded force-advance is disabled for them (see constant).
  useEffect(() => {
    if (!data || !attemptId || data.section) return;
    const id = window.setTimeout(() => {
      void advanceRef.current(true);
    }, QUESTION_TIME_LIMIT_MS);
    return () => window.clearTimeout(id);
  }, [attemptId, data, position]);

  // Active-deadline countdown (whole-attempt when non-sectioned; the current
  // section's deadline when sectioned — the backend denormalizes both onto
  // expires_at).
  // EF-9/FEAT-11: `untimed` is PER POSITION, so a statement has no deadline to
  // count down even though the attempt carries one (the hidden 24 h bound, or
  // — on a mixed attempt — the technical deadline it was handed across from,
  // which is by then in the past).
  const remainingMs = useCountdown(
    data?.untimed ? undefined : data?.expires_at,
  );

  // Reset the time-up latch when the deadline changes so the next section's
  // expiry is handled afresh. Safe as a separate effect from the hook's tick:
  // the countdown's setState flushes after all of this commit's effects, so
  // the latch is already clear before the time-up effect below can observe
  // the new deadline's remainingMs.
  useEffect(() => {
    if (!data?.expires_at) return;
    timeUpHandledRef.current = false;
    preDeadlineSavedRef.current = false;
  }, [data?.expires_at]);

  // #39/#84: land the on-screen answer BEFORE the deadline — the time-up save
  // below always arrives after it and is refused. See preDeadlineSave.ts.
  // Provisional: stored, not finalized, so on a forward-only test the
  // candidate's own Submit in those last seconds is not refused because of it.
  useEffect(() => {
    if (!data || data.untimed || isLockedSubmitted(data)) return;
    if (!shouldSaveBeforeDeadline(remainingMs, preDeadlineSavedRef.current, isAnswered)) {
      return;
    }
    preDeadlineSavedRef.current = true;
    saveCurrent({ provisional: true });
  }, [data, isAnswered, remainingMs, saveCurrent]);

  useEffect(() => {
    // FEAT-11 — never on a clockless position. A candidate handed ACROSS the
    // boundary by a technical lapse arrives at statement 1 with `expires_at`
    // already in the past; handleTimeUp would then run a SECOND time, and
    // there `position < questionnaire_starts_at` is false, so it falls
    // through and routes to Done — losing the questionnaire the handoff
    // existed to protect. Belt and braces with the hook above returning null.
    if (data?.untimed) return;
    if (remainingMs !== 0) return;
    void handleTimeUp();
  }, [remainingMs, handleTimeUp, data?.untimed]);

  if (!attemptId) return null;

  // Error is classified BEFORE the loading/!data guard, and the order is
  // load-bearing. React Query leaves `data` undefined on a failed fetch, so a
  // leading `if (isLoading || !data)` swallows every branch below it and the
  // candidate stares at a skeleton forever — which is what happened here until
  // 2026-08-26: the section_locked bounce, the 409/410 hand-off to Done and the
  // 404 recovery were all unreachable. SectionReview.tsx has always had this
  // order; Question and Review had drifted.
  if (error) {
    // Classified off ApiError's status/detail, never by substring-matching the
    // formatted message — see classifyQuestionError for the two candidate
    // lockouts that cost us (a 404 "Question position out of range" used to
    // wipe a live attempt's only handle).
    const action = classifyQuestionError(error);
    if (action === "bounce") {
      navigate(`/t/${token}`, { replace: true, state: { from: "bounce" } });
      return null;
    }
    // CP-18: the attempt is paused for a between-section break, so no question
    // may be shown. Rebuild the break screen from the section bounds the 409
    // carries (a stale tab, or the gate's question-1 resume, lands here with no
    // router state of its own). Without bounds the review screen cannot render,
    // so fall back to the gate bounce rather than guess.
    if (action === "break") {
      const paused = pausedSectionOf(error);
      if (paused) {
        navigate(`/t/${token}/section-review`, {
          replace: true,
          state: {
            sectionStart: paused.start_position,
            sectionEnd: paused.end_position,
            sectionIndex: paused.index,
            isLast: paused.is_last,
            paused: true,
          },
        });
      } else {
        navigate(`/t/${token}`, { replace: true, state: { from: "bounce" } });
      }
      return null;
    }
    // EF-2: the attempt is paused (a stale tab, or the gate's stored-id resume
    // that always tries question 1). Rebuild the paused screen from the 409.
    if (action === "paused") {
      const paused = pausedAttemptOf(error);
      if (paused) {
        goPaused(paused);
      } else {
        navigate(`/t/${token}`, { replace: true, state: { from: "bounce" } });
      }
      return null;
    }
    if (action === "done") {
      navigate(`/t/${token}/done`, { replace: true });
      return null;
    }
    // The stored attempt id is junk: the server does not recognise it (voided by
    // allow-retake, erased under GDPR Art.17, or belonging to a rotated
    // invitation), or it is not even a UUID. Since the gate now resumes from it
    // (FRONTEND-REACT-8), leaving it in place would trap the candidate — every
    // reload bounces them straight back here. Drop it and run the normal flow.
    if (action === "clear-and-bounce") {
      bounceToGate(token, navigate);
      return null;
    }
    return (
      <div className="mx-auto max-w-3xl p-6 text-sm text-destructive">
        Failed to load the question. Please refresh the page.
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const isCode = data.snapshot.type === "code";
  const isMcqMulti = data.snapshot.type === "mcq_multi";
  const isOpenEnded = data.snapshot.type === "open_ended";
  const isFileUpload = data.snapshot.type === "file_upload";
  // EP-19: the picture frozen with this question, fetched by POSITION through
  // our own origin (the attempt rides the query — an <img> sends no header).
  // On a warmed position it waits for the stamping GET (the route serves only
  // a started position); until then a blank of the same size holds its place.
  const clockPending = awaitsClock(attemptId, position, stampingPosition);
  const picture = pictureFrom(data.snapshot.image, (image) =>
    clockPending
      ? reservedPictureSrc(image)
      : candidatePictureSrc(token, position, attemptId ?? "", image),
  );
  // EF-9: a questionnaire statement (shares the mcq selection).
  const isLikert = data.snapshot.type === "likert";
  // EF-11: the recruiter allowed leaving the window on THIS question (the
  // task needs another program). Fullscreen is still tracked — every exit is
  // recorded, the server marks it relaxed and never flags it — but the
  // blocking overlay stays away so the candidate can actually work.
  const relaxed =
    isFileUpload && Boolean(data.snapshot.file_upload_rules?.relax_anticheat);
  // FEAT-25: the frozen checks, now from the payload itself.
  const checks = resolveAnticheat(data.anticheat);
  const atSectionEnd = section ? position >= section.end_position : false;
  // Back is offered within a section (per_section) or anywhere past Q1 (global)
  // when the test allows return. per_question has single-question sections, so
  // position > start_position is always false → no Back (flag inert there).
  const canGoBack =
    data.allow_back_navigation &&
    (section ? position > section.start_position : position > 1);
  // "Review" jump-to-overview is a free-navigation, global-only affordance.
  // Hidden on the last question (its primary button already routes to Review).
  const canReview =
    !section && data.allow_back_navigation && position < data.total;
  // per_section: section-local "Question X of Y". per_question + global:
  // whole-test count (each per_question unit is a single question, so a
  // section-local count would always read "1 of 1").
  // FEAT-11: inside the trailing questionnaire the count is statement-local.
  // Continuing "Question 14 of 22" over a statement would call a
  // self-description a question and mix two populations in one denominator.
  const inQuestionnaire =
    data.questionnaire_starts_at != null &&
    position >= data.questionnaire_starts_at;
  const displayPosition = inQuestionnaire
    ? position - data.questionnaire_starts_at! + 1
    : section && !perQuestion
      ? position - section.start_position + 1
      : position;
  const displayTotal = inQuestionnaire
    ? data.total - data.questionnaire_starts_at! + 1
    : section && !perQuestion
      ? section.end_position - section.start_position + 1
      : data.total;
  // Optional + unanswered → "Skip". Otherwise the verb tracks the navigation
  // policy: free-nav saves a draft ("Save"), forward-only finalizes ("Submit").
  // Sectioned modes keep the existing wording (free-nav is inert there).
  const lockedSubmitted = isLockedSubmitted(data);
  let primaryLabel: string;
  if (lockedSubmitted) {
    primaryLabel = "Continue";
  } else if (!data.answer_required && !isAnswered) {
    primaryLabel = "Skip";
  } else if (section && !perQuestion) {
    primaryLabel = atSectionEnd ? "Finish section" : "Submit & next";
  } else if (perQuestion) {
    primaryLabel = position >= data.total ? "Submit & finish" : "Submit & next";
  } else if (position >= data.total) {
    primaryLabel = data.allow_back_navigation
      ? "Review all"
      : "Submit & finish";
  } else {
    primaryLabel = data.allow_back_navigation ? "Save & next" : "Submit & next";
  }

  // The fullscreen gate is inescapable and must always win the stack: the
  // paste notice is hidden while the gate is up (state survives, so it
  // re-appears once fullscreen is restored) and rendered BEFORE it in DOM
  // order so a same-z-index tie can never put the notice on top.
  // FEAT-25: no gate at all on a test whose fullscreen check is off — the
  // hook is inert then, so its `inFullscreen` is not a reading to act on.
  const fullscreenOverlayShown =
    checks.fullscreen && fullscreenSupported && !inFullscreen && !relaxed;

  return (
    <div className="flex h-full flex-col bg-background">
      {checks.camera && (
        <CameraPreview
          caption={
            data.camera_snapshots
              ? snapshotCaption(data.camera_snapshots.interval_s)
              : undefined
          }
        />
      )}
      {pasteNoticeOpen && !fullscreenOverlayShown && (
        <PasteDetectedModal onDismiss={() => setPasteNoticeOpen(false)} />
      )}
      {/* EF-2: the Pause confirm. Hidden while the fullscreen gate is up (the
          gate always wins the stack) and rendered before it, like the paste
          notice. No portal — must stay inside the translate="no" subtree. */}
      {pauseDialogOpen && !fullscreenOverlayShown && (
        <ConfirmDialog
          title="Pause the test?"
          body={
            <>
              <p>
                Your answers so far are saved and the clock stops. You&rsquo;ll
                continue on this same question.
              </p>
              <p>
                To come back, open the same link from your invitation email
                &mdash; on this device or another &mdash; within{" "}
                {data.pause_resume_window_hours} hours. After that the test
                can no longer be reopened.
              </p>
              <p>
                Each pause is recorded and shown to the recruiter, along with
                how long you were away.
              </p>
            </>
          }
          confirmLabel="Pause"
          pendingLabel="Pausing…"
          confirmVariant="default"
          onConfirm={() => void pauseNow()}
          onCancel={() => setPauseDialogOpen(false)}
          isPending={pausing}
          error={pauseError}
        />
      )}
      {/* FEAT-15: the confirm before the final submit. Stacked like the Pause
          confirm above — under the fullscreen gate, no portal. */}
      {submitConfirmOpen && isFinalSubmit && !fullscreenOverlayShown && (
        <SubmitConfirmDialog
          summary={
            submitSummaryQuery.data
              ? submitSummary(submitSummaryQuery.data.answers, {
                  position,
                  answered: isAnswered,
                })
              : submitSummaryQuery.isError
                ? null
                : "loading"
          }
          // Same formatter and same condition as the header clock below.
          timeLeft={
            remainingMs !== null && !data.untimed
              ? formatHMS(remainingMs)
              : null
          }
          onConfirm={() => void advance(false)}
          onCancel={() => setSubmitConfirmOpen(false)}
          isPending={submitting}
          error={submitConfirmError}
        />
      )}
      {fullscreenOverlayShown && (
        <FullscreenBlockingOverlay
          hasEverEntered={hasEverEntered}
          cause={fullscreenExitCause}
          onEnter={() => void enterFullscreen()}
        />
      )}
      {/* 2026-09-27 pre-ship B1: below md the actions take their own row. At
          375, Back + Review + "Save & next" are 290 px of a 327 px row, so on
          one line they clipped the primary button, panned the page 38 px and
          squeezed the position label to nothing. */}
      <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border bg-card px-6 py-3 md:flex-nowrap">
        {/* B1 (2026-09-21 browser QA): `min-w-0` lets this box shrink below
            its content at 375, and without `overflow-hidden` the nowrap line
            below simply painted outside it — straight over the countdown.
            Clipping here is what B19's own comment already asked for. */}
        <div className="min-w-0 overflow-hidden">
          {section && !perQuestion && (
            <div className="truncate text-xs font-medium text-muted-foreground">
              Section{" "}
              <span className="text-foreground">{section.index + 1}</span> of{" "}
              {section.total}
              <span className="mx-1.5 text-border">•</span>
              <span className="text-foreground">{section.name}</span>
            </div>
          )}
          {/* B19 (2026-09-12 browser QA): at 375 this wrapped to two lines
              ("Question" / "1 of 4") because the parent is min-w-0 and the
              Pause + Back buttons take priority. It is four words — keep it on
              one line and let the section line above it be the one that
              truncates. `truncate` carries that `whitespace-nowrap` AND the
              ellipsis, so when the buttons do win it reads as a cut rather
              than as a glyph sliced in half (B1). */}
          <div className="truncate text-sm text-muted-foreground">
            {inQuestionnaire ? "Statement" : "Question"}{" "}
            <span className="font-medium text-foreground">
              {displayPosition}
            </span>{" "}
            of{" "}
            <span className="font-medium text-foreground">{displayTotal}</span>
          </div>
        </div>
        {/* EF-9: a statement has no time limit for the candidate — the hidden
            server bound is never shown. FEAT-11: `untimed` is per position, so
            on a mixed attempt the clock shows over the questions and
            disappears the moment the questionnaire starts. */}
        {remainingMs !== null && !data.untimed && (
          <div
            className={cn(
              // B19: `shrink-0` so the timer keeps the header's gap-4 instead
              // of being squeezed until the clock icon abuts "Question".
              "flex shrink-0 items-center gap-1.5 font-mono text-sm tabular-nums",
              remainingMs <= 60_000
                ? "font-semibold text-destructive"
                : "text-muted-foreground",
            )}
            aria-label={
              // A shared clock (CP-18) is the whole test's, even though the
              // attempt is sectioned.
              section && !perQuestion && !data.shared_clock
                ? "Section time remaining"
                : "Time remaining"
            }
            role="timer"
          >
            <Clock className="h-4 w-4" aria-hidden="true" />
            {formatHMS(remainingMs)}
          </div>
        )}
        <div className="flex w-full flex-wrap items-center justify-end gap-2 md:w-auto md:flex-nowrap">
          {/* EF-2: only when the attempt froze pause_enabled (invitation link
              + the test's switch + the Core plan) — never on a shared link. */}
          {data.pause_enabled && (
            <Button
              variant="outline"
              onClick={() => {
                setPauseError(null);
                setPauseDialogOpen(true);
              }}
              disabled={submitting || pausing}
              className="cursor-pointer"
            >
              <Pause className="h-4 w-4" aria-hidden="true" />
              Pause
            </Button>
          )}
          {canGoBack && (
            <Button
              variant="outline"
              onClick={() => void goBack()}
              disabled={submitting}
              className="cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back
            </Button>
          )}
          {canReview && (
            <Button
              variant="outline"
              onClick={() => void goReview()}
              disabled={submitting}
              className="cursor-pointer"
            >
              Review
            </Button>
          )}
          <Button
            onClick={() => requestAdvance()}
            disabled={submitting || (data.answer_required && !isAnswered)}
            className="cursor-pointer"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              primaryLabel
            )}
          </Button>
        </div>
      </header>

      {/* OPS-28 #2 / #30: a transition that failed on a LIVE attempt — said
          here, under the header, with the way to try again. Placeholder copy
          (wording is Emilia's). */}
      {timeUpError && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 border-b border-destructive/30 bg-destructive/5 px-6 py-2 text-sm"
        >
          <span className="text-destructive">{timeUpError}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setTimeUpError(null);
              void handleTimeUp();
            }}
            className="cursor-pointer"
          >
            Retry
          </Button>
        </div>
      )}
      {advanceError && !timeUpError && (
        <div
          role="alert"
          className="border-b border-destructive/30 bg-destructive/5 px-6 py-2 text-sm text-destructive"
        >
          {advanceError}
        </div>
      )}
      {(unitSaveFailures.failures.length > 0 || unitSaveFailures.refused.length > 0) && (
        <div className="border-b border-border px-6 py-2">
          <AnswerSaveFailureNote
            failures={unitSaveFailures.failures}
            refused={unitSaveFailures.refused}
            onRetry={() => void unitSaveFailures.retry()}
            retrying={unitSaveFailures.retrying}
          />
        </div>
      )}
      {/* #112: reopened by URL on a forward-only test — say so instead of
          letting an edit vanish. Placeholder copy (wording is Emilia's). */}
      {lockedSubmitted && (
        <div
          role="status"
          className="border-b border-border bg-muted px-6 py-2 text-sm text-muted-foreground"
        >
          You&rsquo;ve already submitted this question. Answers can&rsquo;t be
          changed on this test &mdash; press Continue to go on.
        </div>
      )}
      {/* The camera preview is a fixed thumbnail bottom-right; without room
          below the content it sat over "Run tests", the last option and the
          results panel, with nothing to scroll them clear (pre-ship
          2026-10-07, B4). The padding is its height plus its caption. */}
      <main
        className={cn(
          "flex-1 overflow-y-auto",
          checks.camera && CAMERA_TILE_RESERVE,
        )}
      >
        {isFileUpload ? (
          <FileUploadAnswer
            promptMd={data.snapshot.prompt_md}
            picture={picture}
            rules={data.snapshot.file_upload_rules ?? null}
            saved={savedFile}
            token={token}
            attemptId={attemptId ?? ""}
            position={position}
            finalOnceUploaded={!data.allow_back_navigation}
            showLeaveNote={checks.fullscreen || checks.activity}
            beginFilePicker={(pos) =>
              anticheatRef.current?.beginFilePicker(pos) ?? (() => {})
            }
            onSaved={(file) => {
              setSavedFile(file);
              queryClient.setQueryData<QuestionResponse>(
                queryKeys.question(token, attemptId ?? "", position),
                (old) => (old ? { ...old, saved_file: file } : old),
              );
              void queryClient.invalidateQueries({
                queryKey: queryKeys.review(token, attemptId ?? null),
              });
            }}
          />
        ) : isCode ? (
          <>
          <CodeQuestion
            promptMd={data.snapshot.prompt_md}
            picture={picture}
            exampleTests={data.snapshot.example_tests ?? []}
            challengeToken={data.snapshot.challenge_token}
            language={codeLanguage}
            setupCode={data.snapshot.setup_code}
            // Editor's initial content (Monaco defaultValue — read ONCE, when
            // the editor first mounts; a sandbox Retry keeps that editor).
            // B5: the code kept across a reload first, then the candidate's
            // saved code so back-nav / jump-back restores their work; fall
            // back to starter.
            starterCode={
              restoredDraft?.kind === "code"
                ? restoredDraft.code
                : (data.saved_final_code ?? data.snapshot.starter_code ?? "")
            }
            onChange={onCodeChange}
            // FEAT-31: set only while this attempt records a replay — it
            // shows the "recorded" caption and wires the editor's paste hooks.
            replayHooks={replayConfig ? replayHooks : undefined}
            onRun={onRun}
            running={running}
            results={results}
            pyodideLoading={pyodideLoading}
            pyodideError={pyodideError}
            onRetryPyodide={loadPyodideRuntime}
            runLocation={RUN_LOCATION[codeLanguage]}
            compileError={compileError}
            runFailure={runFailure}
            // FEAT-18: Reset goes back to the question's OWN starter (which
            // already carries the cheat-proof token line). Not offered on a
            // submitted position of a forward-only test — nothing typed there
            // can be saved any more.
            resetTo={
              lockedSubmitted ? undefined : (data.snapshot.starter_code ?? "")
            }
            onReset={onResetCode}
            helpIntro={previewQuery.data?.code_editor_instructions}
            onHelpOpen={() => setHelpWanted(true)}
            monitored={checks.activity}
            overlaysBlocked={fullscreenOverlayShown}
            // FEAT-29: the candidate's own test cases. A change to the rows
            // drops the results of the Run before it (they no longer line up).
            ownTests={{
              tests: ownTests,
              onChange: (next) => {
                if (next.length !== ownTests.length) setOwnResults(null);
                setOwnTests(next);
              },
              results: ownResults,
              contract: questionContract(
                codeLanguage,
                data.snapshot.function_signature,
              ),
            }}
          />
          {code.length > CODE_MAX && (
            // R13: past the server's cap the save is refused. ⚠ Placeholder
            // copy (pre-ship 2026-10-18) — the wording is Emilia's.
            <WarningNote role="alert" className="mt-3 text-sm">
              Your code is longer than {CODE_MAX.toLocaleString()} characters and
              can&rsquo;t be saved. Shorten it to keep your answer.
            </WarningNote>
          )}
          </>
        ) : isOpenEnded ? (
          <OpenEnded
            promptMd={data.snapshot.prompt_md}
            picture={picture}
            value={freeText}
            onChange={onFreeTextChange}
          />
        ) : isMcqMulti ? (
          <MCQMulti
            promptMd={data.snapshot.prompt_md}
            picture={picture}
            options={data.snapshot.options ?? []}
            selected={mcqMultiSelection}
            onSelect={onSelectMulti}
          />
        ) : isLikert ? (
          <Likert
            promptMd={data.snapshot.prompt_md}
            options={data.snapshot.options ?? []}
            selected={mcqSelection}
            onSelect={onSelectSingle}
            instructions={data.likert_instructions}
          />
        ) : (
          <MCQ
            promptMd={data.snapshot.prompt_md}
            picture={picture}
            options={data.snapshot.options ?? []}
            selected={mcqSelection}
            onSelect={onSelectSingle}
          />
        )}
      </main>
    </div>
  );
}
